import React, { FC, useCallback } from 'react';
import * as Styled from './Diagnostics.styles';
import * as TrackingService from '@modules/tracking/service';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import { pipe } from 'fp-ts/function';
import HeaderTitle from '@shared/components/header/HeaderTitle';
import { useTranslations } from '@shared/modules/translation/hooks';
import { useForm } from 'react-hook-form';
import { Diagnostic, DiagnosticsResolved, diagnosticsResolvedSchema } from '@modules/diagnostics/model';
import { zodResolver } from '@hookform/resolvers/zod';
import Seo from '@shared/components/seo/Seo';
import { useSendTask } from '@core/http/hooks';
import { navigateTo } from '@shared/utils/routes';
import { Translations } from '@shared/modules/translation/locales';
import { HttpError, HttpStatusCode } from '@core/http';
import Rating from 'react-rating';
import starEmpty from '@assets/icons/rating/starEmpty.svg';
import starFull from '@assets/icons/rating/starFull.svg';

const emptySymbol = <Styled.DiagnosticsContentImageRating src={starEmpty.src} alt="empty" />;
const fullSymbol = <Styled.DiagnosticsContentImageRating src={starFull.src} alt="full" />;

function getLabelBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.BAD_REQUEST) return translations.diagnostics.resolved.error.badRequest;
        return null;
      },
    ),
  );
}

interface ResolvedProps {
  diagnostic: Diagnostic;
  answerId: string;
}

const Resolved: FC<ResolvedProps> = ({ diagnostic, answerId }) => {
  const translations = useTranslations();

  const [, tracking, error] = useSendTask(TrackingService.tracking);

  const {
    watch,
    formState: { errors },
    setValue,
    register,
  } = useForm<DiagnosticsResolved>({
    resolver: zodResolver(diagnosticsResolvedSchema),
    defaultValues: {
      answerId: answerId,
      score: 0,
      comment: '',
    },
  });

  const handleClick = () =>
    pipe(
      () => tracking({ score: watch('score'), comment: watch('comment') }),
      TE.chainIOK(() => () => {
        return navigateTo('resolvedSuccess', { diagnostic_number: diagnostic.number, answer_id: answerId });
      }),
    )();

  const handleChange = useCallback(
    (value: number) => {
      setValue('score', value);
    },
    [setValue],
  );

  const labelBuilder = getLabelBuilder(error, translations);

  return (
    <Styled.DiagnosticsContainer>
      <Seo title={translations.diagnostics.resolved.title} />

      <HeaderTitle title={translations.diagnostics.resolved.title} />

      <Styled.DiagnosticsContent>
        <Styled.DiagnosticsContentSubtitle>
          {translations.diagnostics.resolved.subtitle}
        </Styled.DiagnosticsContentSubtitle>

        <Styled.DiagnosticsContentErrorMessage error={error} labelBuilder={labelBuilder} />

        <Styled.DiagnosticsContentLabel>{translations.diagnostics.resolved.label}</Styled.DiagnosticsContentLabel>

        <Styled.DiagnosticsContentText>{translations.diagnostics.resolved.text}</Styled.DiagnosticsContentText>

        <Styled.DiagnosticsContentRating>
          {/* @ts-expect-error - Temporary React 18 types fix */}
          <Rating
            initialRating={watch('score')}
            stop={5}
            emptySymbol={emptySymbol}
            fullSymbol={fullSymbol}
            onChange={handleChange}
          />
        </Styled.DiagnosticsContentRating>

        <Styled.DiagnosticsContentComment label={translations.diagnostics.resolved.comment}>
          <Styled.DiagnosticsContentTextArea
            id="comment"
            placeholder={translations.diagnostics.resolved.commentPlaceholder}
            error={!!errors.comment}
            {...register('comment')}
          />
        </Styled.DiagnosticsContentComment>

        <Styled.DiagnosticsContentButtonContainer>
          <Styled.DiagnosticsContentButton onClick={handleClick}>
            {translations.diagnostics.resolved.send}
          </Styled.DiagnosticsContentButton>
        </Styled.DiagnosticsContentButtonContainer>
      </Styled.DiagnosticsContent>
    </Styled.DiagnosticsContainer>
  );
};

export default Resolved;
